import { useEffect, useState } from 'react';
import { Card, Grid, Table, TableBody, TableCell, TableHead, TableHeaderCell, TableRow, Text, Title } from '@tremor/react';
import { Link } from 'react-router-dom';
import { fetchExceptions } from '../api/exceptions';
import { formatMxn } from '../lib/format';
import { t } from '../lib/i18n';
import type { ExceptionsPayload } from '../api/types';
import DrillCell from './DrillCell';

const fmtPct = new Intl.NumberFormat('es-MX', { style: 'percent', minimumFractionDigits: 1, maximumFractionDigits: 1 });

type Tone = 'good' | 'warn' | 'bad';

const TONE_CLASS: Record<Tone, string> = {
  good: 'bg-emerald-50 text-emerald-900 border-emerald-200 dark:bg-emerald-950 dark:text-emerald-200 dark:border-emerald-900',
  warn: 'bg-amber-50 text-amber-900 border-amber-200 dark:bg-amber-950 dark:text-amber-200 dark:border-amber-900',
  bad:  'bg-red-50 text-red-900 border-red-200 dark:bg-red-950 dark:text-red-200 dark:border-red-900',
};

function Chip({ tone, label, value, target, detail }: {
  tone: Tone; label: string; value: string; target: string; detail?: string;
}) {
  return (
    <div className={`rounded-tremor-default border px-3 py-2 ${TONE_CLASS[tone]}`}>
      <div className="text-xs opacity-80">{label}</div>
      <div className="text-xl font-semibold tabular-nums">{value}</div>
      <div className="text-xs opacity-80">{target}{detail ? ` · ${detail}` : ''}</div>
    </div>
  );
}

/**
 * "Atención" — research-blueprint KPI bands (fill rate, dead stock, turn)
 * plus exec exception cards (A-class stockouts, low-margin stores, 90+ AR).
 * Self-fetching; the server strips cards the viewer's permissions exclude,
 * and a null fetch (403 / network) hides the whole section.
 */
export default function ExceptionCards() {
  const [data, setData] = useState<ExceptionsPayload | null>(null);

  useEffect(() => {
    let alive = true;
    fetchExceptions().then((p) => { if (alive) setData(p); });
    return () => { alive = false; };
  }, []);

  if (!data) return null;

  const fill = data.fill_rate;
  const dead = data.dead_stock;
  const turn = data.inventory_turn;
  const outs = data.a_stockouts;
  const lowMargin = data.low_margin_stores ?? [];
  const arRisk = data.ar_risk ?? [];

  const fillTone: Tone | null = fill && fill.rate != null
    ? (fill.rate >= fill.target ? 'good' : fill.rate >= 0.90 ? 'warn' : 'bad')
    : null;
  const deadTone: Tone | null = dead
    ? (dead.pct < dead.target ? 'good' : dead.pct < dead.alarm ? 'warn' : 'bad')
    : null;
  const turnTone: Tone | null = turn
    ? (turn.annualized >= turn.band_low ? 'good' : turn.annualized >= turn.band_low / 2 ? 'warn' : 'bad')
    : null;

  const hasChips = Boolean(fill || dead || turn);
  const hasCards = (outs?.count ?? 0) > 0 || lowMargin.length > 0 || arRisk.length > 0;
  if (!hasChips && !hasCards) return null;

  const allClear =
    data.daemon_ok &&
    (fillTone === null || fillTone === 'good') &&
    (deadTone === null || deadTone === 'good') &&
    (turnTone === null || turnTone === 'good') &&
    !hasCards;

  return (
    <Card data-tour="exceptions">
      <div className="flex flex-wrap items-baseline gap-x-3">
        <Title>{t.exceptions.title}</Title>
        <Text className="text-tremor-content text-sm">
          {t.exceptions.window
            .replace('{days}', String(data.window_days))
            .replace('{from}', data.window_from)
            .replace('{to}', data.window_to)}
        </Text>
      </div>

      {!data.daemon_ok && (
        <Text className="mt-2 text-amber-700 dark:text-amber-400 text-sm">{t.exceptions.daemonDown}</Text>
      )}

      {hasChips && (
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
          {fill && (
            <Chip
              tone={fillTone ?? 'warn'}
              label={t.exceptions.fill.label}
              value={fill.rate == null ? '—' : fmtPct.format(fill.rate)}
              target={t.exceptions.fill.target}
              detail={fill.rate == null
                ? t.exceptions.fill.noData
                : t.exceptions.fill.detail
                    .replace('{sold}', String(fill.sold_lines))
                    .replace('{lost}', String(fill.lost_lines))}
            />
          )}
          {dead && (
            <Chip
              tone={deadTone ?? 'warn'}
              label={t.exceptions.dead.label}
              value={fmtPct.format(dead.pct)}
              target={t.exceptions.dead.target}
              detail={dead.dead_cost_mxn != null ? formatMxn(dead.dead_cost_mxn) : undefined}
            />
          )}
          {turn && (
            <Chip
              tone={turnTone ?? 'warn'}
              label={t.exceptions.turn.label}
              value={`${turn.annualized.toFixed(1)}×`}
              target={t.exceptions.turn.target}
            />
          )}
        </div>
      )}

      {allClear && <Text className="mt-3 text-emerald-700 dark:text-emerald-400">{t.exceptions.allClear}</Text>}

      {hasCards && (
        <Grid numItemsLg={hasCards && lowMargin.length > 0 ? 3 : 2} className="gap-4 mt-4">
          {outs && outs.count > 0 && (
            <div>
              <Text className="font-semibold text-tremor-content-strong">
                {t.exceptions.stockouts.title} · {outs.count}
              </Text>
              <Text className="text-xs text-tremor-content">{t.exceptions.stockouts.subtitle}</Text>
              <Table className="mt-2">
                <TableHead>
                  <TableRow>
                    <TableHeaderCell>{t.exceptions.stockouts.colPart}</TableHeaderCell>
                    <TableHeaderCell>{t.exceptions.stockouts.colStore}</TableHeaderCell>
                    <TableHeaderCell className="text-right">{t.exceptions.stockouts.colUnits}</TableHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {outs.items.slice(0, 8).map((it) => (
                    <TableRow key={`${it.sid}-${it.line}-${it.part}`}>
                      <TableCell>
                        <DrillCell
                          target={{ kind: 'inv', sid: it.sid, line: it.line, part: it.part }}
                          className="underline"
                        >
                          {it.line} {it.part}
                        </DrillCell>
                      </TableCell>
                      <TableCell>{it.sid}</TableCell>
                      <TableCell className="text-right tabular-nums">{it.units90}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {outs.count > 8 && (
                <Text className="text-xs text-tremor-content mt-1">
                  {t.exceptions.stockouts.more.replace('{n}', String(outs.count - 8))}
                </Text>
              )}
            </div>
          )}

          {lowMargin.length > 0 && (
            <div>
              <Text className="font-semibold text-tremor-content-strong">{t.exceptions.lowMargin.title}</Text>
              <Text className="text-xs text-tremor-content">{t.exceptions.lowMargin.subtitle}</Text>
              <Table className="mt-2">
                <TableBody>
                  {lowMargin.map((s) => (
                    <TableRow key={s.sid}>
                      <TableCell>{s.store_name || `Tienda ${s.sid}`}</TableCell>
                      <TableCell className="text-right tabular-nums text-red-600 dark:text-red-400">
                        {fmtPct.format(s.margin_pct)}
                      </TableCell>
                      <TableCell className="text-right text-xs text-tremor-content">
                        {t.exceptions.lowMargin.chain.replace('{pct}', fmtPct.format(s.chain_margin_pct))}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {arRisk.length > 0 && (
            <div>
              <Text className="font-semibold text-tremor-content-strong">{t.exceptions.arRisk.title}</Text>
              <Text className="text-xs text-tremor-content">{t.exceptions.arRisk.subtitle}</Text>
              <Table className="mt-2">
                <TableHead>
                  <TableRow>
                    <TableHeaderCell>{t.exceptions.arRisk.colAccount}</TableHeaderCell>
                    <TableHeaderCell className="text-right">{t.exceptions.arRisk.col90}</TableHeaderCell>
                    <TableHeaderCell className="text-right">{t.exceptions.arRisk.colTotal}</TableHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {arRisk.map((c) => (
                    <TableRow key={String(c.anum)}>
                      <TableCell>
                        <Link to={`/ar?q=${encodeURIComponent(String(c.anum))}`} className="text-tremor-brand underline">
                          {c.name || c.anum}
                        </Link>
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-red-600 dark:text-red-400 font-semibold">
                        {formatMxn(c.b90_mxn)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{formatMxn(c.total_mxn)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </Grid>
      )}
    </Card>
  );
}
